import React, { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { finApi, finErr, money } from "@/lib/financeApi";
import { btnGold, Empty, field } from "@/components/nds/windows/finance/parts";

export const LINE_BUCKETS = [["escrow", "Escrow (rent)"], ["overhead", "Overhead"], ["program_fee", "Program fee"], ["payroll", "Payroll"]];
const LABEL = Object.fromEntries(LINE_BUCKETS);

function AddLine({ invoiceId, onDone }) {
  const [l, setL] = useState({ description: "", quantity: "1", unit_amount: "", bucket: "escrow" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setL((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await finApi.post(`/invoices/${invoiceId}/lines`, { ...l, quantity: Number(l.quantity), unit_amount: Number(l.unit_amount) }); toast.success("Line added"); setL({ description: "", quantity: "1", unit_amount: "", bucket: l.bucket }); onDone(); }
    catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="fin-inv-line-form" className="grid grid-cols-[1fr_80px_110px_140px_auto] gap-2 items-center">
      <input required value={l.description} onChange={set("description")} placeholder="Description" data-testid="fin-inv-line-desc" className={field} />
      <input type="number" required min="0.01" step="0.01" value={l.quantity} onChange={set("quantity")} data-testid="fin-inv-line-qty" className={field} />
      <input type="number" required min="0" step="0.01" value={l.unit_amount} onChange={set("unit_amount")} placeholder="Unit $" data-testid="fin-inv-line-unit" className={field} />
      <select value={l.bucket} onChange={set("bucket")} data-testid="fin-inv-line-bucket" className={field}>
        {LINE_BUCKETS.map(([k, lb]) => <option key={k} value={k}>{lb}</option>)}
      </select>
      <button type="submit" disabled={busy} data-testid="fin-inv-line-submit" className={`${btnGold} inline-flex items-center gap-1`}><Plus className="h-3 w-3" /> Add</button>
    </form>
  );
}

export default function InvoiceLinesTab({ inv, reload }) {
  return (
    <div data-testid="fin-inv-lines-tab" className="p-5 space-y-3">
      {inv.status === "draft" ? <AddLine invoiceId={inv.id} onDone={reload} /> : <p className="text-[11px] text-nex-muted">Lines are locked once an invoice leaves draft.</p>}
      {inv.lines.length ? (
        <div className="border border-white/10 rounded-sm overflow-x-auto">
          <table data-testid="fin-inv-lines" className="w-full text-left">
            <thead><tr className="font-mono text-[9px] tracking-[0.16em] uppercase text-nex-muted">
              {["Description", "Bucket", "Qty", "Unit", "Line total"].map((h, i) => <th key={h} className={`px-3 py-2 font-normal ${i > 1 ? "text-right" : ""}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {inv.lines.map((l) => (
                <tr key={l.id} data-testid={`fin-inv-line-${l.id}`} className="border-t border-white/5">
                  <td className="px-3 py-2 text-sm text-nex-silver">{l.description}
                    {l.line_bill != null && <div className="font-mono text-[10px] text-nex-muted">billed {money(l.line_bill)} · paid resident {money(l.line_pay)} · overhead {money(l.agency_overhead)}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] uppercase text-nex-muted">{LABEL[l.bucket] || l.bucket}</td>
                  <td className="px-3 py-2 font-mono text-xs text-right text-nex-muted">{l.quantity}</td>
                  <td className="px-3 py-2 font-mono text-xs text-right text-nex-muted">{money(l.unit_amount)}</td>
                  <td className="px-3 py-2 font-mono text-xs text-right text-nex-silver">{money(l.line_total)}</td>
                </tr>
              ))}
              <tr className="border-t border-nex-gold/30">
                <td colSpan={4} className="px-3 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted text-right">Invoice total</td>
                <td data-testid="fin-inv-lines-total" className="px-3 py-2 font-mono text-sm text-right text-nex-gold">{money(inv.total_amount)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : <Empty testid="fin-inv-lines-empty">No line items yet.</Empty>}
    </div>
  );
}
